// src/utils/invoiceNetRevenue.js — netrev-6f2a8c53 (R1d-5)
// Revenue NET of credit notes, for the reports (Marcus, R1 decision 7: "reports subtract
// creditedTotal"). A "reduce the sale" refund leaves the invoice's total, amount paid and status
// alone and records the credit in invoice.creditedTotal (tax included, like invoice.total) — a
// field only the server can write (firestore.rules). Until this seam no report read it, so a
// credited sale still counted in full.
//
// THE ZERO-CHANGE RULE: an invoice with no credit note has no creditedTotal, and every function
// here then returns EXACTLY what the report computed before (the same expression, no rounding).
// A company that has never issued a credit note sees identical reports.

const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;

/** Credit notes issued against this invoice, tax included. 0 for every invoice without one. */
function creditedTotalOf(invoice) {
    const c = Number(invoice && invoice.creditedTotal);
    return Number.isFinite(c) && c > 0 ? c : 0;
}

/** The invoice's total as the reports read it (parseFloat(total) || 0), less its credit notes. */
function netInvoiceTotal(invoice) {
    const total = parseFloat(invoice && invoice.total) || 0;
    const credited = creditedTotalOf(invoice);
    return credited ? r2(total - credited) : total;
}

/**
 * Cash kept from one invoice, as the Sales report counts it: a Paid invoice counts its total,
 * any other its amountPaid (the report's own rule, unchanged) — less the money handed back
 * against credit notes (a credit note never lowers amountPaid).
 */
function netCashCollected(invoice) {
    const inv = invoice || {};
    const cash = inv.status === 'Paid' ? (parseFloat(inv.total) || 0) : (inv.amountPaid ? parseFloat(inv.amountPaid) : 0);
    const credited = creditedTotalOf(inv);
    return credited ? r2(Math.max(0, cash - credited)) : cash;
}

/**
 * Credit notes per job, for the Job Profitability list (whose revenue is the job's own
 * invoiceTotal figure, not its invoices). An invoice belongs to a job by linkedJobId, jobId, or
 * the job's invoiceId pointer — the same three links the job's detail page follows. Each invoice
 * counts once per job. Returns { [jobId]: credited } with entries only for jobs that have some.
 */
function creditedByJob(jobs, creditedInvoices) {
    const out = {};
    const invoices = (creditedInvoices || []).filter((inv) => inv && creditedTotalOf(inv) > 0);
    if (!invoices.length) return out;
    for (const job of jobs || []) {
        if (!job || !job.id) continue;
        let sum = 0;
        for (const inv of invoices) {
            if (inv.linkedJobId === job.id || inv.jobId === job.id || (job.invoiceId && inv.id === job.invoiceId)) sum += creditedTotalOf(inv);
        }
        if (sum > 0) out[job.id] = r2(sum);
    }
    return out;
}

/** A job's revenue on the Job Profitability list: its invoiceTotal figure, less its credit notes. */
function netJobRevenue(job, creditedForJob) {
    const revenue = parseFloat(job && job.invoiceTotal) || 0;
    const credited = Number(creditedForJob) > 0 ? Number(creditedForJob) : 0;
    return credited ? r2(Math.max(0, revenue - credited)) : revenue;
}

/**
 * revbridge-3c9e7a15 — one invoice's credit notes as lines: [{ number, amount }] (amount positive,
 * tax included). Read from its credit-note payment lines; if the invoice carries a creditedTotal
 * those lines don't account for, the remainder is one unnumbered line, so the lines always add up
 * to creditedTotalOf(invoice). [] for an invoice without a credit note.
 */
function creditNoteLinesOf(invoice) {
    const credited = creditedTotalOf(invoice);
    if (!credited) return [];
    const rows = Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory.filter((r) => r && r.isCreditNote === true) : [];
    const lines = rows.map((r) => ({ number: String(r.reference || ''), amount: r2(Math.abs(Number(r.amount) || 0)) })).filter((l) => l.amount > 0);
    const listed = r2(lines.reduce((s, l) => s + l.amount, 0));
    if (Math.abs(listed - credited) > 0.005) return [{ number: '', amount: r2(credited) }];
    return lines;
}

/**
 * revbridge-3c9e7a15 — what stands between a job's invoiced items and its Total Revenue on the
 * job's profitability page: the tax its invoices charged, then each credit note. tax is null when
 * no invoice charged any (nothing is shown then). label: the invoices' one tax name ("HST ON"),
 * or "Tax" when there are several or none recorded.
 */
function revenueBridge(invoices) {
    const list = Array.isArray(invoices) ? invoices.filter(Boolean) : [];
    const taxAmount = r2(list.reduce((s, inv) => { const t = Number(inv.taxAmount); return s + (Number.isFinite(t) && t > 0 ? t : 0); }, 0));
    const names = new Set();
    for (const inv of list) for (const li of Array.isArray(inv.lineItems) ? inv.lineItems : []) {
        const n = li && !li.taxExempt && li.taxSnapshot && Number(li.taxSnapshot.rate) > 0 ? String(li.taxSnapshot.name || '').trim() : '';
        if (n) names.add(n);
    }
    return {
        tax: taxAmount > 0 ? { label: names.size === 1 ? [...names][0] : 'Tax', amount: taxAmount } : null,
        credits: list.flatMap(creditNoteLinesOf),
    };
}

/**
 * jobadds-5d1b9c37 — a job with NO invoice: its page shows the job's own price as revenue (the
 * job's invoiceTotal figure), so the lines above Total Revenue are the job's own line items, and
 * whatever of that figure they don't account for is its tax. → { items: [{ name, quantity, price,
 * amount }], tax: { label: 'Tax', amount } | null }. With no line items the list is empty.
 */
function jobOwnRevenueLines(job, revenue) {
    const lines = Array.isArray(job && job.lineItems) ? job.lineItems.filter(Boolean) : [];
    const items = lines.map((li) => {
        const quantity = parseFloat(li.quantity) || 0; const price = parseFloat(li.price) || 0;
        return { name: li.name || li.description || 'Item', quantity, price, amount: quantity * price };
    });
    const rest = r2((Number(revenue) || 0) - items.reduce((s, i) => s + i.amount, 0));
    return { items, tax: items.length > 0 && rest > 0.005 ? { label: 'Tax', amount: rest } : null };
}

/**
 * jobadds-5d1b9c37 — what the listed lines still don't explain: Total Revenue less (the items shown
 * + tax − credit notes). 0 when they add up (within half a cent). The page shows a non-zero value
 * as one "Other" line, so every job page adds up to its Total Revenue.
 */
function revenueRemainder(revenue, itemsTotal, tax, credits) {
    const explained = (Number(itemsTotal) || 0) + (tax ? tax.amount : 0) - (credits || []).reduce((s, c) => s + c.amount, 0);
    const rest = r2((Number(revenue) || 0) - explained);
    return Math.abs(rest) > 0.005 ? rest : 0;
}

/** revbridge-3c9e7a15 — a job's credit notes as lines, for the list's expanded row (same three links as creditedByJob). */
function creditNoteLinesForJob(job, creditedInvoices) {
    if (!job || !job.id) return [];
    return (creditedInvoices || [])
        .filter((inv) => inv && (inv.linkedJobId === job.id || inv.jobId === job.id || (job.invoiceId && inv.id === job.invoiceId)))
        .flatMap(creditNoteLinesOf);
}

// ───────────────────────────────────────────────────────────────────────────────────────────────
// cnperiod-5a3e7d92 — CREDIT NOTES COUNT IN THE PERIOD THEY HAPPEN (Marcus, 2026-10-13; BACKLOG
// row 21). A credit note reduces revenue in the month of ITS OWN date — the date the refund was
// recorded — not the month of the invoice it corrects. That is how QuickBooks dates the refund
// receipt and how the adjustment falls on the GST/HST return. Until this seam the reports took a
// credit note off the INVOICE's month (netInvoiceTotal above), so a January sale credited in March
// re-wrote January.
//
// A credit note's date is on its payment-history line (the server writes `date` = when the refund
// was recorded). When an invoice's lines don't account for its creditedTotal, the whole credit is
// one undated line and falls on the fallback date the caller gives (the invoice's own date).
// THE ZERO-CHANGE RULE still holds: with no credit note anywhere, every list here is empty and each
// report computes exactly what it did before.

/** A JS Date from a Firestore Timestamp, a Date, an ISO string or a millisecond number; null when it isn't one. */
function toDateOrNull(v) {
    if (!v) return null;
    if (typeof v.toDate === 'function') { const d = v.toDate(); return d instanceof Date && !Number.isNaN(d.getTime()) ? d : null; }
    const d = v instanceof Date ? v : new Date(v);
    return Number.isNaN(d.getTime()) ? null : d;
}

/** One invoice's credit notes with their dates: [{ number, amount, date }] (amount positive, tax included). */
function datedCreditNotesOf(invoice, fallbackDate) {
    const credited = creditedTotalOf(invoice);
    if (!credited) return [];
    const fallback = toDateOrNull(fallbackDate);
    const rows = Array.isArray(invoice.paymentHistory) ? invoice.paymentHistory.filter((r) => r && r.isCreditNote === true) : [];
    const lines = rows.map((r) => ({ number: String(r.reference || ''), amount: r2(Math.abs(Number(r.amount) || 0)), date: toDateOrNull(r.date) || toDateOrNull(r.createdAt) || fallback })).filter((l) => l.amount > 0);
    const listed = r2(lines.reduce((s, l) => s + l.amount, 0));
    if (Math.abs(listed - credited) > 0.005) return [{ number: '', amount: r2(credited), date: fallback }];
    return lines;
}

/**
 * Every credit note dated from `start` to `end` (inclusive), across these invoices — whatever month
 * each invoice itself belongs to. → [{ invoice, number, amount, date }]
 *   dateOf(invoice)  the report's own rule for an invoice's date (the fallback for an undated credit)
 */
function creditNotesInPeriod(invoices, start, end, dateOf) {
    const out = [];
    for (const inv of invoices || []) {
        if (!inv || !creditedTotalOf(inv)) continue;
        for (const c of datedCreditNotesOf(inv, typeof dateOf === 'function' ? dateOf(inv) : null)) {
            if (c.date && c.date >= start && c.date <= end) out.push({ invoice: inv, ...c });
        }
    }
    return out;
}

/** The invoice's total exactly as the reports read it: parseFloat(total) || 0. Credit notes are taken off separately, by their own date. */
function grossInvoiceTotal(invoice) {
    return parseFloat(invoice && invoice.total) || 0;
}

/** Cash received on one invoice by the Sales report's own rule (Paid: the total; otherwise amountPaid). Money handed back is taken off separately, by the credit note's date. */
function grossCashCollected(invoice) {
    const inv = invoice || {};
    return inv.status === 'Paid' ? (parseFloat(inv.total) || 0) : (inv.amountPaid ? parseFloat(inv.amountPaid) : 0);
}

/** Does this invoice belong to this job? The three links the job's own page follows. */
function invoiceIsForJob(invoice, job) {
    return !!invoice && !!job && !!job.id && (invoice.linkedJobId === job.id || invoice.jobId === job.id || (!!job.invoiceId && invoice.id === job.invoiceId));
}

/**
 * Job Profitability list. The list shows jobs COMPLETED in the selected dates; a credit note counts
 * in the dates it was ISSUED. So, for the credit notes of these invoices:
 *   byJob[jobId]   the credit notes of a LISTED job — each marked inPeriod or not. Only the inPeriod
 *                  ones are taken off that row's revenue; the others belong to another period and
 *                  are shown on the row for information.
 *   outside        credit notes issued IN the period for a job that is NOT listed (completed in an
 *                  earlier period, or not completed): taken off the period's totals as their own line.
 * → { byJob: { [jobId]: [{ number, amount, date, inPeriod }] }, outside: [{ number, amount, date, jobNumber }] }
 */
function jobCreditNotesForPeriod(listedJobs, allJobs, creditedInvoices, start, end, dateOf) {
    const byJob = {}; const outside = [];
    for (const inv of creditedInvoices || []) {
        if (!inv || !creditedTotalOf(inv)) continue;
        const notes = datedCreditNotesOf(inv, typeof dateOf === 'function' ? dateOf(inv) : null);
        const listed = (listedJobs || []).find((j) => invoiceIsForJob(inv, j));
        for (const c of notes) {
            const inPeriod = !!c.date && c.date >= start && c.date <= end;
            if (listed) { (byJob[listed.id] = byJob[listed.id] || []).push({ ...c, inPeriod }); continue; }
            if (!inPeriod) continue;
            const other = (allJobs || []).find((j) => invoiceIsForJob(inv, j));
            outside.push({ ...c, jobNumber: other ? (other.jobNumber || '') : '' });
        }
    }
    return { byJob, outside };
}

/** Is this payment-history line the internal +credit-note line (not money from the customer)? */
const isCreditNoteLine = (row) => !!row && row.isCreditNote === true;

export { toDateOrNull, datedCreditNotesOf, creditNotesInPeriod, grossInvoiceTotal, grossCashCollected, invoiceIsForJob, jobCreditNotesForPeriod };
export { creditedTotalOf, netInvoiceTotal, netCashCollected, creditedByJob, netJobRevenue, isCreditNoteLine, creditNoteLinesOf, revenueBridge, creditNoteLinesForJob, jobOwnRevenueLines, revenueRemainder };
